import { AHIcon, AHSeparator, AHText, Box, ListItem, useBoxStyle } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";
import { useSetCurrentStoreId } from "../../../OS/Login/State";
import { FlatList } from "react-native";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import { useCallback, useMemo, useState } from "react";
import { useOrgHomeDetailLayout } from "../useOrgHomeDetailContainerStyle";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { Features, hasFeatureInTier } from "../../../useHasFeatureInTier";

import { OrgSearchInput } from "../OrgSearchInput";
import { WorkspaceDetailHeader } from "../WorkspaceDetailHeader";

import { WorkspaceEmptyListItem } from "../WorkspaceEmptyListItem";

import { OrgStoreWorkspaceBreadcrumbs } from "./OrgStoreWorkspaceBreadcrumbs";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { OrgLogoutButton } from "../OrgLogoutButton";
import { OrgCompactStoresQuery } from "./__generated__/OrgCompactStoresQuery.graphql";

export function OrgStoresCompactScreen(props: OrgHomeScreenProps<"OrgStores">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerOrgStoresScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerOrgStoresScreen(
  props: OrgHomeScreenProps<"OrgStores"> & { fetchKey: string }
) {
  const intl_tokens = useIntlTokens();
  const [search, setSearch] = useState("");

  const data = useLazyLoadQuery<OrgCompactStoresQuery>(
    graphql`
      query OrgCompactStoresQuery {
        me {
          ...OrgLogoutButton_me
        }
        organisation {
          name
          rootWorkspace {
            stores {
              id
              name
              tier
              timezone
              workspaceBreadcrumbs(includeExternalId: true)
              ...OrgStoreWorkspaceBreadcrumbs_store
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const setCurrentStoreId = useSetCurrentStoreId();

  const workspace = data.organisation?.rootWorkspace;
  const listData = useMemo(() => {
    const stores =
      workspace?.stores?.slice().sort((a, b) => {
        const aBc = a.workspaceBreadcrumbs.join(" ").toLowerCase();
        const bBc = b.workspaceBreadcrumbs.join(" ").toLowerCase();
        if (aBc === bBc) {
          return a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()
            ? 1
            : -1;
        }

        return aBc > bBc ? 1 : -1;
      }) ?? [];
    if (search.length > 0) {
      const normalizedSearch = search.toLocaleLowerCase();
      return stores.filter((s) =>
        s.name.toLocaleLowerCase().includes(normalizedSearch)
      );
    }
    return stores;
  }, [search, workspace?.stores]);
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <ListItem
          border="border-primary"
          isLast={index === listData.length - 1}
          left={<AHIcon type="store" color="text-washed" />}
          right={
            <>
              <OrgStoreWorkspaceBreadcrumbs store={item} />
              <ListItem.NavArrow />
            </>
          }
          onPress={() => {
            setCurrentStoreId(item.id, item.timezone);

            if (hasFeatureInTier(Features.OPERATIONS_PLATFORM, item.tier)) {
              props.navigation.navigateDeprecated("Team", {});
            } else {
              props.navigation.navigateDeprecated("Dashboard", {});
            }
          }}
        >
          <Box
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <ListItem.Title>{item.name}</ListItem.Title>
          </Box>
        </ListItem>
      );
    },
    [listData.length, props.navigation, setCurrentStoreId]
  );

  const detailLayout = useOrgHomeDetailLayout();
  const insets = useSafeAreaInsets();
  const contentContainerStyle = useBoxStyle({
    spaceHorizontal: "medium",
    spaceBottom: "medium",

    insets: {
      bottom: insets.bottom,
    },
  });

  return (
    <FlatList
      style={[
        {
          flex: 1,
          marginBottom: detailLayout.keyboardBottomInset,
          marginTop: insets.top,
        },
      ]}
      contentContainerStyle={contentContainerStyle}
      data={listData}
      keyboardShouldPersistTaps="handled"
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
      ListEmptyComponent={WorkspaceEmptyListItem}
      ListFooterComponent={
        !detailLayout.keyboardShown && data.me ? (
          <Box
            insets={{ bottom: insets.bottom }}
            spaceVertical="medium"
            spaceTop="large"
          >
            <OrgLogoutButton me={data.me} />
          </Box>
        ) : null
      }
      ListHeaderComponent={
        <Box color="background">
          <WorkspaceDetailHeader title={intl_tokens.Units} />
          <OrgSearchInput value={search} onChangeText={setSearch} />
          <Box color="background">
            <ListItem disabled border="border-primary">
              <AHText color="darkerText" numberOfLines={1} type="subtitle">
                {intl_tokens.Name}
              </AHText>
            </ListItem>
            <AHSeparator />
          </Box>
        </Box>
      }
    />
  );
}
